import React, { useState } from "react";

import { faMagnifyingGlass, faRobot } from "@fortawesome/pro-regular-svg-icons";

import { SessionNamespace, useDebugSession } from "src/api/agents/debug";
import { AgentFailedBanner } from "src/base-components/agents/AgentFailedBanner";
import { AgentStatusPill } from "src/base-components/agents/AgentStatusPill";
import { ChatMessageList } from "src/base-components/Chat/ChatMessageList";
import { ChatMessageSkeleton } from "src/base-components/Chat/ChatMessageSkeleton";
import { DisplayDateTime } from "src/base-components/Date";
import { DocumentTitle } from "src/base-components/DocumentTitle";
import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import { Option, Select } from "src/base-components/Select";
import {
  SessionMessagesResponseMessagesInner,
  SessionResponse,
  SessionStatus,
} from "src/clients/agent-management";
import { Button } from "src/design-system/Button";
import { EmptyState } from "src/design-system/EmptyState";
import { Input } from "src/design-system/Input";
import { SubHeader } from "src/flow/SubHeader";
import { usePowertools } from "src/powertools/hooks";
import { ErrorPage } from "src/router/ErrorPage";
import { SessionMetricsPanel } from "src/sessionDebug/SessionMetricsPanel";
import { useSessionDebugSearchParams } from "src/sessionDebug/useSessionDebugSearchParams";
import { UnifiedCopilotMessages } from "src/unifiedCopilot/chat/UnifiedCopilotMessages";

const KIND_OPTIONS: Option[] = [
  { key: "agent", value: "Agent" },
  { key: "copilot", value: "Copilot" },
];

export const SessionDebugPage: React.FC = () => {
  const hasPowertools = usePowertools();

  if (!hasPowertools) {
    return <ErrorPage message="404: Page not found" />;
  }

  return (
    <>
      <DocumentTitle segments={["Session debugger"]} />
      <FloatingWindowsProvider>
        <SessionDebugPageContent />
      </FloatingWindowsProvider>
    </>
  );
};

const SessionDebugPageContent: React.FC = () => {
  const { kind, sessionId, setDebugParams } = useSessionDebugSearchParams();
  const sessionQuery = useDebugSession(sessionId, kind);

  return (
    <div
      className="decideScrollbar flex h-full w-full flex-col overflow-y-auto"
      data-loc="session-debug-page"
    >
      <SubHeader title="Session debugger">
        {/* Remount on URL changes so the inputs always mirror the URL. */}
        <SessionDebugForm
          key={`${kind}:${sessionId ?? ""}`}
          kind={kind}
          sessionId={sessionId}
          onSubmit={setDebugParams}
        />
      </SubHeader>
      <SessionTraceBody
        hasSessionId={!!sessionId}
        isError={sessionQuery.isError}
        isLoading={sessionQuery.isLoading}
        session={sessionQuery.data ?? null}
      />
    </div>
  );
};

const SessionDebugForm: React.FC<{
  kind: SessionNamespace;
  sessionId: string | null;
  onSubmit: (values: {
    kind: SessionNamespace;
    sessionId: string | null;
  }) => void;
}> = ({ kind, sessionId, onSubmit }) => {
  // Both fields are drafts: nothing loads until the form is submitted.
  const [kindDraft, setKindDraft] = useState(kind);
  const [sessionIdDraft, setSessionIdDraft] = useState(sessionId ?? "");

  return (
    <form
      className="flex items-center gap-2 pr-4"
      data-loc="session-debug-form"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({ kind: kindDraft, sessionId: sessionIdDraft.trim() || null });
      }}
    >
      <div className="w-32">
        <Select
          dataLoc="session-debug-kind"
          options={KIND_OPTIONS}
          placeholder="Kind"
          value={kindDraft}
          onChange={(value) => setKindDraft(value as SessionNamespace)}
        />
      </div>
      <div className="w-80">
        <Input
          dataLoc="session-debug-session-id"
          placeholder="Session ID"
          value={sessionIdDraft}
          variant="monospaced"
          onChange={(event) => setSessionIdDraft(event.target.value)}
        />
      </div>
      <Button dataLoc="session-debug-load" htmlType="submit" size="sm">
        Load
      </Button>
    </form>
  );
};

const toChatMessages = (
  messages: SessionMessagesResponseMessagesInner[],
): SessionMessagesResponseMessagesInner[] =>
  messages.map((message) => {
    if (message.type !== "human") return message;

    // Agent/regular Copilot sessions carry the user input as `invocation_message` +
    // `structured_input` instead of `content_blocks`; the chat view only
    // renders blocks (and drops block-less human messages), so synthesize
    // them. The bubble renders markdown, hence the fenced JSON.
    const baseBlocks = message.content_blocks?.length
      ? message.content_blocks
      : message.invocation_message
        ? [{ type: "text" as const, text: message.invocation_message }]
        : [];
    const structuredInputBlock = message.structured_input
      ? [
          {
            type: "text" as const,
            text: `\n\n\`\`\`json\n${JSON.stringify(message.structured_input, null, 2)}\n\`\`\``,
          },
        ]
      : [];
    const contentBlocks = [...baseBlocks, ...structuredInputBlock];

    return contentBlocks.length
      ? { ...message, content_blocks: contentBlocks }
      : message;
  });

const SessionTraceBody: React.FC<{
  hasSessionId: boolean;
  isError: boolean;
  isLoading: boolean;
  session: SessionResponse | null;
}> = ({ hasSessionId, isError, isLoading, session }) => {
  if (!hasSessionId) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <EmptyState
          dataLoc="session-debug-empty"
          description="Enter a session ID and pick its kind to view the trace."
          headline="No session loaded"
          icon={faMagnifyingGlass}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-5xl flex-col px-5 py-4">
        <div className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-white p-4">
          <ChatMessageSkeleton />
          <ChatMessageSkeleton />
          <ChatMessageSkeleton />
        </div>
      </div>
    );
  }

  if (isError || !session) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <EmptyState
          dataLoc="session-debug-error"
          description="The session could not be loaded. A session only resolves under its own namespace — check the kind."
          headline="Unable to load session"
          icon={faRobot}
          variant="error"
        />
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-0 w-full max-w-5xl flex-1 flex-col overflow-hidden px-5 pb-4">
      <div
        className="flex items-center gap-4 pt-4"
        data-loc="session-debug-status"
      >
        <div className="flex items-center gap-2">
          <span className="font-inter-normal-12px text-gray-500">Status</span>
          <AgentStatusPill substatus={session.status} />
        </div>
        <div className="flex items-center gap-2">
          <span className="font-inter-normal-12px text-gray-500">Created</span>
          <span className="font-inter-normal-12px text-gray-800">
            <DisplayDateTime value={session.created_at} />
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-inter-normal-12px text-gray-500">Started</span>
          <span className="font-inter-normal-12px text-gray-800">
            <DisplayDateTime value={session.started_at} />
          </span>
        </div>
        {session.finished_at && (
          <div className="flex items-center gap-2">
            <span className="font-inter-normal-12px text-gray-500">
              Finished
            </span>
            <span className="font-inter-normal-12px text-gray-800">
              <DisplayDateTime value={session.finished_at} />
            </span>
          </div>
        )}
      </div>
      {session.status === SessionStatus.FAILED && (
        <AgentFailedBanner
          terminationMessage={session.termination_message ?? null}
          terminationReason={session.termination_reason ?? null}
        />
      )}
      <SessionMetricsPanel session={session} />
      <div className="mt-4 flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
        <ChatMessageList dataLoc="session-debug-chat">
          <UnifiedCopilotMessages
            isActive={false}
            messages={toChatMessages(session.messages ?? [])}
          />
        </ChatMessageList>
      </div>
    </div>
  );
};
